import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Check, AlertTriangle, FileDown, RefreshCw, ArrowLeft, X, Plus, Lock, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { OnboardingTip } from '../components/OnboardingTip';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AppShell } from '../components/Layout';
import { linkedge, downloadFile, downloadAdminAuditReportByAnalysis, api } from '../lib/api';
import { billingApi } from '../lib/billing';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import ProfileOptimizer from '../components/results/ProfileOptimizer';
import PostsResults from '../components/results/PostsResults';
import InspirationTab from '../components/results/InspirationTab';
import PromptPostsTab from '../components/results/PromptPostsTab';
import OverviewDashboard from '../components/results/OverviewDashboard';
import { SectionLevelGaps } from '../components/results/SectionLevelGaps';
import PovPostsTab from '../components/results/PovPostsTab';
import { FullAuditBody } from '../components/results/FullAuditReport';
import AnalysisV2Layout from './analysis-v2/AnalysisV2Layout';
import BlayzBot from '../components/dashboard/BlayzBot';
import { SendCompAuditDialog } from '../components/admin/SendCompAuditDialog';
import CompAuditViewsPanel from '../components/admin/CompAuditViewsPanel';
import { TONE_OPTIONS } from '../lib/toneOptions';
import { POST_COUNTRY_OPTIONS, resolveDefaultCountry } from '../lib/countries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';

export default function AnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [refreshingResearch, setRefreshingResearch] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [me, setMe] = useState(null);
  const [compAuditOpen, setCompAuditOpen] = useState(false);
  const pollRef = useRef(null);
  const researchPollRef = useRef(null);
  const promptTabRef = useRef(null);

  const fetchData = async () => {
    try {
      const d = await linkedge.getAnalysis(id);
      setData(d);
      return d;
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load analysis'));
      return null;
    }
  };

  useEffect(() => {
    fetchData();
    billingApi.me().then(setMe).catch(() => {});
    const refreshMe = () => billingApi.me().then(setMe).catch(() => {});
    window.addEventListener('billing:refresh', refreshMe);
    // Hard 12-minute poll cap so a truly hung backend can never leave
    // the audit spinner running forever. The backend watchdog will flip
    // stalled analyses to `failed` after 10 minutes of no progress, so
    // this is the client-side safety net (extra 2 min buffer to see
    // the server-side flip). Once flipped, the normal `failed` branch
    // renders with the retry CTA.
    const AUDIT_MAX_POLL_MS = 12 * 60 * 1000;
    const startedAt = Date.now();
    let hardStopped = false;
    pollRef.current = setInterval(async () => {
      const d = await fetchData();
      if (d && (d.status === 'complete' || d.status === 'failed') && d.posts_status !== 'regenerating') {
        clearInterval(pollRef.current);
        return;
      }
      if (!hardStopped && Date.now() - startedAt > AUDIT_MAX_POLL_MS && d?.status === 'running') {
        hardStopped = true;
        clearInterval(pollRef.current);
        // Surface a stall banner in-place so the user isn't stranded.
        setData((prev) => prev ? { ...prev, status: 'failed', error: prev?.error || 'Your audit is taking unusually long \u2014 please start a new one. No charge for the stalled run.' } : prev);
        try { toast.error('Audit stalled \u2014 please start a new one.'); } catch {}
      }
    }, 2500);
    return () => {
      clearInterval(pollRef.current);
      if (researchPollRef.current) clearInterval(researchPollRef.current);
      window.removeEventListener('billing:refresh', refreshMe);
    };
    // eslint-disable-next-line
  }, [id]);

  const onRegenerate = async (opts) => {
    setRegenerateOpen(false);
    setRegenerating(true);
    try {
      await linkedge.regeneratePosts(id, opts);
      const countLabel = opts?.count ? ` (${opts.count} posts)` : '';
      toast.success(`Regenerating posts${countLabel}...`);
      if (pollRef.current) clearInterval(pollRef.current);
      const startedAt = Date.now();
      // Hard 6-minute poll cap so a hung backend can never leave the
      // frontend spinner running forever. The recovery sweep on the
      // next backend restart will flip stuck jobs to error too — this
      // is the client-side belt to the server-side braces.
      const MAX_POLL_MS = 6 * 60 * 1000;
      pollRef.current = setInterval(async () => {
        const d = await fetchData();
        if (d?.posts_status === 'ready' || d?.posts_status === 'error') {
          clearInterval(pollRef.current);
          setRegenerating(false);
          if (d.posts_status === 'ready') toast.success('Fresh posts ready!');
          else toast.error(d?.posts_error || 'Regeneration failed — please try again.');
          return;
        }
        if (Date.now() - startedAt > MAX_POLL_MS) {
          clearInterval(pollRef.current);
          setRegenerating(false);
          toast.error('Post generation is taking unusually long. Refresh the page in a minute, or try again.');
        }
      }, 2500);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not regenerate'));
      setRegenerating(false);
    }
  };

  const onRefreshInspiration = async (opts) => {
    try {
      await linkedge.refreshInspiration(id, opts);
      toast.success('Refreshing inspiration with your filters...');
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const d = await fetchData();
        if (d?.inspiration_status === 'ready' || d?.inspiration_status === 'error') {
          clearInterval(pollRef.current);
          if (d.inspiration_status === 'ready') toast.success('Inspiration updated!');
          else toast.error('Refresh failed');
        }
      }, 2500);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not refresh'));
    }
  };

  const onInspirationDataChange = (patch) => {
    if (!patch || typeof patch !== 'object') return;
    setData((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const onRefreshResearch = async () => {
    if (refreshingResearch) return;
    setRefreshingResearch(true);
    try {
      await linkedge.refreshResearch(id);
      toast.success('Refreshing sources from across the web...');
      if (researchPollRef.current) clearInterval(researchPollRef.current);
      researchPollRef.current = setInterval(async () => {
        const d = await fetchData();
        if (d?.research_status === 'ready' || d?.research_status === 'error') {
          clearInterval(researchPollRef.current);
          setRefreshingResearch(false);
          if (d.research_status === 'ready') {
            const n = (d.research_bundle?.citations || []).length;
            toast.success(`Sources refreshed — ${n} citation${n === 1 ? '' : 's'} loaded`);
          } else {
            toast.error('Source refresh failed — please try again');
          }
        }
      }, 2500);
    } catch (err) {
      setRefreshingResearch(false);
      toast.error(getErrorMessage(err, 'Could not refresh sources'));
    }
  };

  const onDownload = async (format) => {
    setDownloading(format);
    try {
      await downloadFile(id, format);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const onDownloadBranded = async (format) => {
    const key = `branded-${format}`;
    setDownloading(key);
    const t = toast.loading(`Preparing branded ${format.toUpperCase()}…`);
    try {
      await downloadAdminAuditReportByAnalysis(
        id,
        data?.profile_name || 'audit',
        'full',
        format,
      );
      toast.success(`Branded ${format.toUpperCase()} downloaded`, { id: t });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id: t });
    } finally {
      setDownloading(null);
    }
  };

  if (error) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <AlertTriangle size={36} className="mx-auto text-destructive mb-3" />
          <h2 className="text-xl font-semibold">{error}</h2>
          <Button onClick={() => navigate('/dashboard')} className="mt-6">Back to dashboard</Button>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  const isDone = data.status === 'complete';
  const isFailed = data.status === 'failed';
  // Trust the backend's progress_pct when present — it accounts for
  // the currently-running stage (half-credit) so the bar visibly
  // advances during long LLM calls. Fall back to stage-count math for
  // legacy audits.
  const backendPct = typeof data.progress_pct === 'number' ? data.progress_pct : null;
  const completedStages = (data.stages || []).filter((s) => s.status === 'complete').length;
  const totalStages = (data.stages || []).length || 3;
  const progress = backendPct != null
    ? Math.min(100, Math.max(0, backendPct))
    : Math.min(100, Math.round((completedStages / totalStages) * 100));

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="analysis-back-button">
              <ArrowLeft size={16} className="mr-2" /> Dashboard
            </Button>
            {(me?.role === 'admin' || me?.role === 'editor') && isDone && (
              <div className="mt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCompAuditOpen(true)}
                  className="rounded-full text-[11px] tracking-wide"
                  data-testid="send-comp-audit-open"
                >
                  Send comp audit &rarr;
                </Button>
              </div>
            )}
            <h1 className="mt-2 text-3xl font-bold" data-testid="analysis-title">{data.profile_name || 'Your Analysis'}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <p className="text-sm text-muted-foreground">
                Status: <span className="font-medium" data-testid="analysis-status">{data.status}</span>
              </p>
              {data.audit_context?.objective && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-primary"
                  data-testid="analysis-audit-context-chip"
                  title="Objective / status this audit was scoped to"
                >
                  {String(data.audit_context.status || '').replace('_', '/')}
                  <span className="opacity-50">·</span>
                  {String(data.audit_context.objective).replace(/_/g, ' ')}
                </span>
              )}
              {data.is_legacy && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-amber-600 dark:text-amber-400"
                  data-testid="analysis-legacy-chip"
                  title="This audit was created with the retired URL-import flow. New audits use your uploaded LinkedIn PDF and the deterministic scoring engine, so results may look different."
                >
                  Legacy analysis
                </span>
              )}
            </div>
          </div>
          {isDone && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => onDownloadBranded('pdf')}
                disabled={downloading === 'branded-pdf'}
                data-testid="results-download-branded-pdf-button"
              >
                {downloading === 'branded-pdf'
                  ? <Loader2 size={16} className="animate-spin" />
                  : <FileDown size={16} className="mr-2" />} Branded PDF
              </Button>
              <Button
                variant="secondary"
                onClick={() => onDownloadBranded('pptx')}
                disabled={downloading === 'branded-pptx'}
                data-testid="results-download-branded-pptx-button"
              >
                {downloading === 'branded-pptx'
                  ? <Loader2 size={16} className="animate-spin" />
                  : <FileDown size={16} className="mr-2" />} Branded PPTX
              </Button>
            </div>
          )}
          {isDone && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  if (activeTab === 'manus-posts') {
                    promptTabRef.current?.triggerGenerate?.();
                  } else {
                    setRegenerateOpen(true);
                  }
                }}
                disabled={regenerating}
                className="rounded-xl"
                data-testid="results-regenerate-posts-button"
              >
                {(() => {
                  const noPostsYet = data.posts_deferred || !(Array.isArray(data.posts) && data.posts.length > 0);
                  const label = noPostsYet ? 'Generate posts' : 'Regenerate posts';
                  return regenerating
                    ? <Loader2 size={16} className="animate-spin" />
                    : <><RefreshCw size={16} className="mr-2" /> {label}</>;
                })()}
              </Button>
              <OnboardingTip
                id="action-regenerate-posts"
                title="Regenerate posts"
                what="Re-runs Researched Posts with new angles, custom topics, source URLs, or different tones."
                expect={[
                  'Counts against your monthly post quota (each new post = 1)',
                  'You can supply up to 10 custom URLs to ground the posts',
                  'Pick which tones to rotate (or leave blank to auto-vary)',
                  'Existing posts stay in place until the new batch finishes',
                ]}
                placement="left"
              />
            </div>
          )}
        </div>

        {!isDone && !isFailed && (
          <ProgressPanel data={data} progress={progress} />
        )}

        {isFailed && (
          <Card className="border-destructive/40">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertTriangle size={20} className="text-destructive shrink-0" />
              <div>
                <h3 className="font-semibold">Analysis failed</h3>
                <p className="text-sm text-muted-foreground mt-1">{data.error || 'Unknown error'}</p>
                <Button className="mt-4" onClick={() => navigate('/dashboard')}>Start a new analysis</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isDone && (
          <>
            <CompAuditViewsPanel
              analysisId={id}
              canView={me?.role === 'admin' || me?.role === 'editor'}
            />
          <ResultsTabs
            data={data}
            regenerating={regenerating}
            onRefreshInspiration={onRefreshInspiration}
            onInspirationDataChange={onInspirationDataChange}
            refreshingResearch={refreshingResearch}
            onRefreshResearch={onRefreshResearch}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            promptTabRef={promptTabRef}
            me={me}
            onUpgrade={() => navigate('/pricing')}
            onDownload={onDownload}
            downloading={downloading}
            onRegenerateRequested={() => setRegenerateOpen(true)}
            onRetryLast={() => onRegenerate({
              count: data?.last_post_count || 10,
              custom_topics: data?.last_custom_topics || [],
              tones: data?.last_tones || [],
              extra_urls: data?.last_extra_urls || [],
              length_mode: data?.last_length_mode === 'long' ? 'long' : 'short',
            })}
          />
          </>
        )}
      </div>
      <RegeneratePostsDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        onSubmit={onRegenerate}
        defaultCount={data?.last_post_count || 10}
        defaultCustomTopics={data?.last_custom_topics || []}
        defaultTones={data?.last_tones || []}
        defaultExtraUrls={data?.last_extra_urls || []}
        defaultCountry={resolveDefaultCountry(
          data?.post_context?.geography,
          data?.profile_input?.country,
          data?.profile_input?.location,
        )}
        defaultLengthMode={data?.last_length_mode === 'long' ? 'long' : 'short'}
        inspirationTopics={(data?.trending_topics || []).filter((t) => t && t.from_leader)}
        positioningReport={data?.inspiration_positioning_report || null}
      />
      <BlayzBot
        context="analysis"
        state={{
          auditReady: isDone,
          hasPostTabs: !!(data?.posts?.length || data?.pov_posts?.length || data?.full_audit_v2),
          careerStage: data?.full_audit_v2?.careerStage,
          careerObjective: data?.full_audit_v2?.selectedObjectiveLabel,
        }}
      />
      <SendCompAuditDialog
        analysisId={id}
        profileDisplay={data?.profile_name}
        open={compAuditOpen}
        onOpenChange={setCompAuditOpen}
      />
    </AppShell>
  );
}

function RegeneratePostsDialog({ open, onClose, onSubmit, defaultCount, defaultCustomTopics, defaultTones, defaultExtraUrls, defaultCountry, defaultLengthMode, inspirationTopics = [], positioningReport = null }) {
  const [count, setCount] = useState(defaultCount);
  const [topics, setTopics] = useState(defaultCustomTopics || []);
  const [tones, setTones] = useState(defaultTones || []);
  const [input, setInput] = useState('');
  const [urlsText, setUrlsText] = useState(() => (defaultExtraUrls || []).join('\n'));
  const [country, setCountry] = useState(defaultCountry || 'Global');
  const [lengthMode, setLengthMode] = useState(defaultLengthMode === 'long' ? 'long' : 'short');
  // Goal-first framework — same 10-goal playbook the Prompt Posts and
  // POV writers use. Optional; empty = classic Researched Posts flow.
  const [goal, setGoal] = useState('');
  const [goalCatalog, setGoalCatalog] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get('/posts/goals').then((r) => setGoalCatalog(r?.data?.goals || [])).catch(() => {});
  }, [open]);
  // Inspiration-sourced topic IDs the user has selected as seed material
  // for THIS generation. Reset every time the dialog opens.
  const [selectedSeedTopics, setSelectedSeedTopics] = useState([]);
  // Gap-analysis seed items the user has selected. Each entry is the index
  // into the flattened ``gapOptions`` list below. Resets per dialog open.
  const [selectedGapIdx, setSelectedGapIdx] = useState([]);

  // Flatten the positioning-report gap analysis into one selectable list.
  // We mix four sources so the user can mix-and-match:
  //   1) per-leader gaps_and_whitespace (with leader name as source)
  //   2) cohort_overview.collective_whitespace (cohort-wide gaps)
  //   3) your_distinctive_position.first_three_post_themes (LLM starter ideas)
  //   4) recommended_post_topics — the 10 cohort-gap-driven topics generated
  //      after the distinctive position. These are the user's publishing
  //      backlog and the most frequent regen seeds.
  const gapOptions = useMemo(() => {
    if (!positioningReport) return [];
    const out = [];
    for (const l of (positioningReport.per_leader || [])) {
      for (const g of (l?.gaps_and_whitespace || [])) {
        if (typeof g === 'string' && g.trim()) {
          out.push({ kind: 'leader-gap', text: g.trim(), source: l.leader_name || 'leader' });
        }
      }
    }
    for (const g of (positioningReport.cohort_overview?.collective_whitespace || [])) {
      if (typeof g === 'string' && g.trim()) {
        out.push({ kind: 'cohort-gap', text: g.trim(), source: 'cohort-wide' });
      }
    }
    for (const t of (positioningReport.your_distinctive_position?.first_three_post_themes || [])) {
      const theme = (t && (t.theme || '')).trim();
      if (theme) {
        out.push({
          kind: 'starter-theme',
          text: theme,
          source: 'your distinctive position',
          why: (t && t.why) || '',
        });
      }
    }
    for (const t of (positioningReport.recommended_post_topics || [])) {
      const topic = (t && (t.topic || '')).trim();
      if (topic) {
        out.push({
          kind: 'recommended-topic',
          text: topic,
          source: (t && t.gap_source) || 'recommended for you',
          why: (t && t.why) || '',
        });
      }
    }
    return out;
  }, [positioningReport]);

  useEffect(() => {
    setCount(defaultCount);
    setTopics(defaultCustomTopics || []);
    setTones(defaultTones || []);
    setUrlsText((defaultExtraUrls || []).join('\n'));
    setCountry(defaultCountry || 'Global');
    setLengthMode(defaultLengthMode === 'long' ? 'long' : 'short');
    setSelectedSeedTopics([]);
    setSelectedGapIdx([]);
  }, [defaultCount, defaultCustomTopics, defaultTones, defaultExtraUrls, defaultCountry, defaultLengthMode, open]);

  const addTopic = () => {
    const v = input.trim();
    if (!v) return;
    if (topics.includes(v)) {
      setInput('');
      return;
    }
    setTopics([...topics, v]);
    setInput('');
  };
  const removeTopic = (t) => setTopics(topics.filter((x) => x !== t));
  const toggleTone = (key) =>
    setTones((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <DialogTitle>Regenerate posts</DialogTitle>
          <DialogDescription>
            Tell us any topics you want covered and how many posts you need. We keep your voice the same.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 px-6 pb-2 overflow-y-auto flex-1 min-h-0" data-testid="regen-dialog-body">
          <div className="space-y-2">
            <Label>Specific topics (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Add up to 10. Each post will try to cover one of these first; the rest pull from the trending library.
            </p>
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                placeholder="e.g., AI agents for sales teams"
                data-testid="regen-topic-input"
              />
              <Button type="button" variant="secondary" onClick={addTopic} data-testid="regen-add-topic">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div
              className="flex flex-wrap gap-2 max-h-40 overflow-y-auto rounded-md border border-border/60 p-2 empty:hidden"
              data-testid="regen-topic-chips"
            >
              {topics.map((t) => (
                <Badge key={t} variant="secondary" className="pl-2.5 pr-1.5 py-1 text-sm max-w-full">
                  <span className="truncate inline-block max-w-[18rem] align-middle" title={t}>{t}</span>
                  <button onClick={() => removeTopic(t)} className="ml-1.5 hover:text-destructive shrink-0" aria-label={`Remove ${t}`}>
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="regen-extra-urls">Research URLs (optional)</Label>
            <p className="text-xs text-muted-foreground">
              Paste up to 10 article / report / post URLs you want us to read and cite. We extract the content and prioritize them as inline citations in your posts.
            </p>
            <Textarea
              id="regen-extra-urls"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={'https://stratechery.com/...\nhttps://hbr.org/...\nhttps://yourcompany.substack.com/...'}
              rows={4}
              className="text-sm font-mono-edge resize-none"
              data-testid="regen-extra-urls"
            />
            <p className="text-[11px] text-muted-foreground">
              One URL per line. Job-board URLs are filtered out automatically.
            </p>
          </div>

          {inspirationTopics.length > 0 && (
            <div className="space-y-2" data-testid="regen-inspiration-section">
              <div className="flex items-center justify-between">
                <Label>Topics from your inspirations</Label>
                <span className="text-[11px] font-mono-edge text-muted-foreground">
                  {selectedSeedTopics.length} / {inspirationTopics.length} selected
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Real topics mined from the leaders you scraped. Select any to seed this generation — each becomes a primary topic for one of the posts.
              </p>
              <div className="max-h-44 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="regen-inspiration-list">
                {inspirationTopics.map((t, idx) => {
                  const checked = selectedSeedTopics.includes(idx);
                  const title = t.title || t.topic || '';
                  return (
                    <label
                      key={idx}
                      className={`flex items-start gap-2 p-2.5 cursor-pointer transition-colors text-xs ${
                        checked ? 'bg-primary/10' : 'hover:bg-secondary/50'
                      }`}
                      data-testid={`regen-inspiration-item-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedSeedTopics((cur) =>
                            cur.includes(idx) ? cur.filter((x) => x !== idx) : [...cur, idx],
                          )
                        }
                        className="mt-0.5 h-3.5 w-3.5 accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium leading-snug">{title}</p>
                        {t.from_leader && (
                          <p className="text-[10px] text-muted-foreground mt-0.5 font-mono-edge">
                            From {t.from_leader}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {gapOptions.length > 0 && (
            <div className="space-y-2" data-testid="regen-gap-section">
              <div className="flex items-center justify-between">
                <Label>Gaps from your positioning analysis</Label>
                <span className="text-[11px] font-mono-edge text-muted-foreground">
                  {selectedGapIdx.length} / {gapOptions.length} selected
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Topics where the selected leaders left whitespace, plus the starter post themes from your distinctive position and the ten recommended-for-you topics. Select any to generate posts that occupy positions no one else owns.
              </p>
              <div className="max-h-44 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="regen-gap-list">
                {gapOptions.map((g, idx) => {
                  const checked = selectedGapIdx.includes(idx);
                  const sourceBadge =
                    g.kind === 'cohort-gap'
                      ? 'Collective whitespace'
                      : g.kind === 'starter-theme'
                      ? 'Starter theme'
                      : g.kind === 'recommended-topic'
                      ? `Recommended · ${g.source}`
                      : `Gap · ${g.source}`;
                  return (
                    <label
                      key={idx}
                      className={`flex items-start gap-2 p-2.5 cursor-pointer transition-colors text-xs ${
                        checked ? 'bg-primary/10' : 'hover:bg-secondary/50'
                      }`}
                      data-testid={`regen-gap-item-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedGapIdx((cur) =>
                            cur.includes(idx) ? cur.filter((x) => x !== idx) : [...cur, idx],
                          )
                        }
                        className="mt-0.5 h-3.5 w-3.5 accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium leading-snug">{g.text}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono-edge">
                          {sourceBadge}
                        </p>
                        {g.why && (
                          <p className="text-[10px] text-muted-foreground italic mt-0.5 leading-snug">
                            {g.why}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2" data-testid="regen-goal-row">
            <div className="flex items-center justify-between">
              <Label>Goal &amp; framework</Label>
              {(() => {
                const g = goalCatalog.find((x) => x.key === goal);
                return g ? <span className="text-[11px] font-mono-edge text-muted-foreground tracking-[0.16em]">{g.framework_name}</span> : null;
              })()}
            </div>
            <p className="text-xs text-muted-foreground">
              Pick a goal and every post in this batch follows that framework&apos;s shape. Optional — leave on Auto for the classic mix.
            </p>
            <Select
              value={goal || '__auto__'}
              onValueChange={(v) => setGoal(v === '__auto__' ? '' : v)}
            >
              <SelectTrigger className="h-10" data-testid="regen-goal-select">
                <SelectValue placeholder="Auto — classic mix" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto — classic mix</SelectItem>
                {goalCatalog.map((g) => (
                  <SelectItem key={g.key} value={g.key} data-testid={`regen-goal-option-${g.key}`}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Post length</Label>
              <span className="text-[11px] font-mono-edge text-muted-foreground tracking-[0.16em]" data-testid="regen-length-value">
                {lengthMode === 'short' ? 'SHORT · 120-200w' : 'LONG · 220-400w'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Short = scene + hidden problem + memorable close. Long = full thought-leadership structure with research support.
            </p>
            <ToggleGroup
              type="single"
              value={lengthMode}
              onValueChange={(v) => v && setLengthMode(v)}
              className="grid grid-cols-2 gap-2"
              data-testid="regen-length-toggle"
            >
              <ToggleGroupItem
                value="short"
                className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-lg border border-border h-10"
                data-testid="regen-length-short"
              >
                Short
              </ToggleGroupItem>
              <ToggleGroupItem
                value="long"
                className="data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-lg border border-border h-10"
                data-testid="regen-length-long"
              >
                Long
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Country / region context</Label>
              <span className="text-[11px] font-mono-edge text-muted-foreground tracking-[0.16em]" data-testid="regen-country-value">
                {country.toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Anchors examples, currency, spelling, and regulators to this market. Saved as the default for this analysis.
            </p>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="regen-country-select">
                <SelectValue placeholder="Pick a country" />
              </SelectTrigger>
              <SelectContent>
                {POST_COUNTRY_OPTIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`regen-country-option-${c.value.toLowerCase().replace(/\s+/g, '-')}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>How many posts</Label>
              <span className="text-sm font-mono-edge font-semibold" data-testid="regen-count-value">{count}</span>
            </div>
            <Slider
              value={[count]}
              min={3}
              max={10}
              step={1}
              onValueChange={(v) => setCount(v[0])}
              data-testid="regen-count-slider"
            />
            <p className="text-xs text-muted-foreground">3-10 posts. Larger batches take longer to generate.</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Tones</Label>
              {tones.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTones([])}
                  className="text-xs text-primary hover:underline"
                  data-testid="regen-tones-clear"
                >
                  Clear (use default mix)
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {tones.length === 0
                ? 'Default: a balanced rotation across all 7 tones. Pick any subset to bias the batch.'
                : `Posts will rotate through your ${tones.length} selected tone${tones.length === 1 ? '' : 's'}.`}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="regen-tones-grid">
              {TONE_OPTIONS.map((t) => {
                const selected = tones.includes(t.key);
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => toggleTone(t.key)}
                    title={t.hint}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      selected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-secondary text-foreground border-border hover:border-primary/40'
                    }`}
                    data-testid={`regen-tone-${t.key}`}
                    aria-pressed={selected}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 border-t border-border bg-background shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const extra_urls = urlsText
              .split(/\r?\n/)
              .map((x) => x.trim())
              .filter(Boolean)
              .slice(0, 10);
            // Resolve the selected seed-topic indices into raw topic strings.
            const seed_topics = selectedSeedTopics
              .map((idx) => (inspirationTopics[idx]?.title || inspirationTopics[idx]?.topic || '').trim())
              .filter(Boolean);
            // Also fold in any selected gap-analysis items. For starter
            // themes we send just the theme line (the "why" stays as
            // context the user can read in the report).
            for (const idx of selectedGapIdx) {
              const g = gapOptions[idx];
              if (g && g.text) seed_topics.push(g.text);
            }
            onSubmit({ custom_topics: topics, count, tones, extra_urls, country_override: country, length_mode: lengthMode, seed_topics, goal: goal || undefined });
          }} className="rounded-xl" data-testid="regen-submit">
            <RefreshCw size={14} className="mr-2" /> Generate {count} posts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProgressPanel({ data, progress }) {
  const stages = data.stages || [];
  const stageMessage = data.stage_message || '';
  return (
    <Card className="border-border overflow-hidden">
      <CardContent className="p-6">
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">We&rsquo;re running the deep audit on your profile. This usually takes 90 seconds to 2 minutes.</p>
            <span className="text-sm font-mono-edge" data-testid="progress-percent">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="analysis-progress-bar" />
          {stageMessage && (
            <p
              className="mt-2.5 text-[12.5px] text-primary/90 flex items-center gap-1.5"
              data-testid="analysis-stage-message"
            >
              <Loader2 size={12} className="animate-spin shrink-0" />
              <span>{stageMessage}</span>
            </p>
          )}
        </div>
        <ol className="space-y-3" data-testid="analysis-progress-stepper">
          {stages.map((s) => (
            <li key={s.key} className="flex items-start gap-3" data-testid={`analysis-stage-${s.key}`}>
              <StageIcon status={s.status} />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {s.label}{' '}
                  {s.status === 'complete' && <span className="text-xs text-muted-foreground ml-1">· done</span>}
                  {s.status === 'in_progress' && <span className="text-xs text-primary ml-1">· in progress</span>}
                </p>
                {s.detail && s.status === 'complete' && (
                  <p className="text-xs text-muted-foreground font-mono-edge mt-0.5" data-testid={`analysis-stage-detail-${s.key}`}>
                    {Object.entries(s.detail).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function StageIcon({ status }) {
  if (status === 'complete')
    return (
      <motion.span
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--success))] text-white shrink-0"
      >
        <Check size={14} />
      </motion.span>
    );
  if (status === 'in_progress')
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-primary shrink-0">
        <Loader2 size={14} className="animate-spin" />
      </span>
    );
  if (status === 'failed')
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-destructive/20 text-destructive shrink-0">
        <AlertTriangle size={14} />
      </span>
    );
  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground shrink-0">
      •
    </span>
  );
}

function ResultsTabs({ data, regenerating, onRefreshInspiration, onInspirationDataChange, refreshingResearch, onRefreshResearch, activeTab, setActiveTab, promptTabRef, me, onUpgrade, onDownload, downloading, onRegenerateRequested, onRetryLast }) {
  // Show proactive "Gold required" hint on Researched/Prompt Posts tabs for
  // users whose plan can't generate new posts (one_time, none, expired).
  const planType = me?.plan_type || 'none';
  const status = me?.status || 'inactive';
  const postsLocked = !(['gold', 'comp'].includes(planType) && status === 'active');
  const postsLockReason = (() => {
    if (planType === 'one_time' && status === 'active') return 'Profile Success Plan covers audits only.';
    if (planType === 'none') return 'Pick a plan to generate posts.';
    return 'Your plan has ended.';
  })();
  // ``contentGated`` = Profile Success (one-time) users see a teaser of the
  // Researched Posts + Inspiration content (top half readable, bottom half
  // blurred + copy disabled). Gold / comp / admin users are unaffected.
  const isAdminOrEditor = me?.role === 'admin' || me?.role === 'editor';
  const contentGated =
    !isAdminOrEditor && planType === 'one_time' && status === 'active';
  // Peer Benchmark unlock: user-level enterprise flag (toggled from
  // admin), per-audit enterprise_view flag (toggled from admin per
  // analysis), or admin/editor role. Any one is enough.
  const peerUnlocked = !!(
    me?.is_enterprise
    || data?.enterprise_view
    || isAdminOrEditor
  );
  // Full Audit v2 analyses use the redesigned single-nav layout with
  // 4 click dropdowns (Profile Audit / Rewrite / Create Content / Grow
  // Presence) plus the task-focused Rewrite workflow.
  const isV2 = !!data.full_audit_v2;
  if (isV2) {
    return (
      <AnalysisV2Layout
        data={data}
        me={me}
        contentGated={contentGated}
        peerUnlocked={peerUnlocked}
        onUpgrade={onUpgrade}
        regenerating={regenerating}
        onRegenerateRequested={onRegenerateRequested}
        onRetryLast={onRetryLast}
        onDownload={onDownload}
        downloading={downloading}
      />
    );
  }
  return (
    <Tabs defaultValue="overview" className="w-full" value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="bg-secondary rounded-xl p-1 flex-wrap" data-testid="results-tabs">
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="overview"
            className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm"
            data-testid="results-tab-overview"
          >
            {isV2 ? 'Audit Report' : 'Overview'}
          </TabsTrigger>
          {!isV2 && (
          <span className="ml-1">
            <OnboardingTip
              id="tab-overview"
              title="Overview"
              what="A premium, single-glance dashboard of your LinkedIn profile assessed against Blayzrr's objective-specific Profile Intelligence Framework."
              expect={[
                'Authority score donut (peer benchmark on Enterprise*)',
                'Profile gaps per category',
                'Percentile in your cohort (Enterprise*)',
                'Top recommendations + content ideas',
              ]}
              inline
            />
          </span>
          )}
        </div>
        {!isV2 && (
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="profile"
            className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm"
            data-testid="results-tab-profile-optimizer"
          >
            Profile Optimizer
          </TabsTrigger>
          <span className="ml-1">
            <OnboardingTip
              id="tab-profile-optimizer"
              title="Profile Optimizer"
              what="A LinkedIn-ready rewrite of your headline, About, skills, and experience based on your audit."
              expect={[
                'Scores for each profile section',
                '3 headline variants you can A/B test',
                'A rewritten About + Skills + JD bullets',
                'Downloadable PDF & DOCX of the full blueprint',
              ]}
              inline
            />
          </span>
        </div>
        )}
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="posts"
            className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm"
            data-testid="results-tab-posts"
          >
            Researched Posts
            {postsLocked && (
              <Lock
                size={11}
                className="ml-1.5 text-muted-foreground"
                aria-label="Requires Executive Thought Leadership Plan"
                data-testid="results-tab-posts-lock"
              />
            )}
          </TabsTrigger>
          <span className="ml-1">
            <OnboardingTip
              id="tab-researched-posts"
              title="Researched Posts"
              what="LinkedIn posts grounded in real, cited sources and written in your voice."
              expect={[
                'Multiple posts with rotating tones (analytical, contrarian, storytelling, etc.)',
                'Inline numbered citations linking to actual articles',
                'A pre-fetched research bundle so generation is fast',
                'Click "Regenerate posts" to refresh with new angles, custom topics, or URLs',
              ]}
              inline
            />
          </span>
        </div>
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="pov"
            className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm"
            data-testid="results-tab-pov"
          >
            POV Posts
            {postsLocked && (
              <Lock
                size={11}
                className="ml-1.5 text-muted-foreground"
                aria-label="Requires Executive Thought Leadership Plan"
                data-testid="results-tab-pov-lock"
              />
            )}
          </TabsTrigger>
          <span className="ml-1">
            <OnboardingTip
              id="tab-pov"
              title="POV Posts"
              what="Sharp, voice-matched opinion posts on a single trending story or article URL."
              expect={[
                'Pick a trending headline or paste any article URL',
                'A LinkedIn-ready POV draft with a strong opening hook',
                'Inline numbered citations to verifiable sources',
                'Full history of every POV you have generated for this audit',
              ]}
              inline
            />
          </span>
        </div>
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="manus-posts"
            className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm"
            data-testid="results-tab-manus-posts"
          >
            Prompt Posts
            {postsLocked && (
              <Lock
                size={11}
                className="ml-1.5 text-muted-foreground"
                aria-label="Requires Executive Thought Leadership Plan"
                data-testid="results-tab-manus-posts-lock"
              />
            )}
          </TabsTrigger>
          <span className="ml-1">
            <OnboardingTip
              id="tab-prompt-posts"
              title="Prompt Posts"
              what="Quick, focused posts (150–200 words) generated from a topic or URLs."
              expect={[
                'You provide a topic + optional source URLs',
                'You pick the tone(s) you want',
                'Posts are returned in the same voice profile you set on Researched Posts',
                'Great for fast, opinion-led drops between researched pieces',
              ]}
              inline
            />
          </span>
        </div>
        <div className="relative inline-flex items-center">
          <TabsTrigger
            value="inspiration"
            className={`rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm ${peerUnlocked ? '' : 'opacity-70'}`}
            data-testid="results-tab-inspiration"
            disabled={!peerUnlocked}
            title={peerUnlocked ? '' : 'Available on the Enterprise Plan'}
          >
            Inspiration
            {!peerUnlocked && (
              <span className="ml-1.5 inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5 text-[9px] font-mono-edge tracking-[0.14em] uppercase">
                Enterprise
              </span>
            )}
          </TabsTrigger>
        </div>
      </TabsList>
      <TabsContent value="overview" className="mt-6">
        {isV2 ? (
          <FullAuditBody data={data} />
        ) : (
          <>
            <SectionLevelGaps headlineGap={data?.headline_gap} aboutGap={data?.about_gap} />
            <div className={data?.headline_gap || data?.about_gap ? 'mt-6' : ''}>
              <OverviewDashboard data={data} me={me} onJumpTab={setActiveTab} />
            </div>
          </>
        )}
      </TabsContent>
      {!isV2 && (
      <TabsContent value="profile" className="mt-6">
        <ProfileOptimizer data={data} />
      </TabsContent>
      )}
      <TabsContent value="posts" className="mt-6">
        {postsLocked && (
          <PostsUpgradeNotice
            reason={postsLockReason}
            onUpgrade={onUpgrade}
            testId="posts-tab-upgrade-notice"
          />
        )}
        <PostsResults
          posts={data.posts || []}
          voice={data.voice_profile}
          regenerating={regenerating || data.posts_status === 'regenerating'}
          analysisId={data.id}
          existingCalendar={data.calendar}
          latestPov={data.latest_pov}
          povHistory={data.pov_history}
          postContext={data.post_context}
          auditPlanContext={data.full_audit_v2 ? {
            presencePlan: data.full_audit_v2.presencePlan,
            thirtyDayPlan: data.full_audit_v2.thirtyDayPlan,
            selectedObjectiveLabel: data.full_audit_v2.selectedObjectiveLabel,
            careerStage: data.full_audit_v2.careerStage,
            targetAudienceOrOpportunity: data.full_audit_v2.targetAudienceOrOpportunity,
          } : null}
          contentGated={contentGated}
          onUpgrade={onUpgrade}
          researchMeta={(() => {
            const rawRole =
              data.blueprint?.job_title?.recommended ||
              data.profile_input?.job_title ||
              data.profile_input?.headline ||
              '';
            // Trim long blueprint suggestions like "CEO, FooCo (B2B SaaS | …)" → "CEO"
            const cleanRole = String(rawRole)
              .split('(')[0]
              .split('|')[0]
              .split(',')[0]
              .trim()
              .slice(0, 60);
            // Owner + admin can refresh the citation pool
            const isOwner = !!(me && data.user_id && me.id === data.user_id);
            const isAdmin = me?.role === 'admin';
            const isEditorAssigned =
              me?.role === 'editor' && (me?.assigned_user_ids || []).includes(data.user_id);
            const canRefresh = isOwner || isAdmin || isEditorAssigned;
            return {
              topicsCount: (data.trending_topics || []).length,
              leadersCount: (data.thought_leaders || []).length,
              sourcesCount: (data.research_bundle?.citations || []).length,
              role: cleanRole,
              industry: data.profile_input?.industry || data.post_context?.industry || '',
              country: data.profile_input?.country || data.post_context?.country || '',
              bundleFetchedAt:
                data.research_refreshed_at ||
                data.research_bundle?.fetched_at ||
                data.created_at ||
                '',
              canRefresh,
              refreshing: refreshingResearch || data.research_status === 'refreshing',
              postsStale: !!data.posts_stale,
              onRefresh: canRefresh ? onRefreshResearch : null,
            };
          })()}
        />
      </TabsContent>
      <TabsContent value="pov" className="mt-6">
        <PovPostsTab
          data={data}
          locked={postsLocked}
          lockReason={postsLockReason}
          onUpgrade={onUpgrade}
          defaultCountry={resolveDefaultCountry(
            data?.post_context?.geography,
            data?.profile_input?.country,
            data?.profile_input?.location,
          )}
          defaultLengthMode={data?.last_pov_length_mode === 'short' ? 'short' : 'long'}
        />
      </TabsContent>
      <TabsContent value="inspiration" className="mt-6">
        <InspirationTab
          leaders={data.thought_leaders || []}
          topics={data.trending_topics || []}
          defaultIndustry={data.profile_input?.industry || ''}
          activeFilters={data.inspiration_filters || {}}
          refreshing={data.inspiration_status === 'refreshing'}
          onRefresh={onRefreshInspiration}
          analysisId={data.id}
          inspirationPins={data.inspiration_pins || []}
          positioningReport={data.inspiration_positioning_report || null}
          onInspirationDataChange={onInspirationDataChange}
          contentGated={contentGated}
          onUpgrade={onUpgrade}
        />
      </TabsContent>
      <TabsContent value="manus-posts" className="mt-6">
        {postsLocked && (
          <PostsUpgradeNotice
            reason={postsLockReason}
            onUpgrade={onUpgrade}
            testId="manus-posts-tab-upgrade-notice"
          />
        )}
        <PromptPostsTab
          ref={promptTabRef}
          analysisId={data.id}
          existing={data.manus_posts}
          defaults={{
            topic: data.last_prompt_topic || '',
            count: data.last_prompt_count || data.manus_posts?.count || 4,
            words_each: data.last_prompt_words_each || data.manus_posts?.words_each || 175,
            custom_topics: data.last_prompt_custom_topics || data.manus_posts?.custom_topics || [],
            tones: data.last_prompt_tones || data.manus_posts?.tones || [],
            extra_urls: data.last_prompt_extra_urls || data.manus_posts?.extra_urls || [],
            country: resolveDefaultCountry(
              data?.post_context?.geography,
              data?.profile_input?.country,
              data?.profile_input?.location,
            ),
          }}
        />
      </TabsContent>
    </Tabs>
  );
}


/**
 * Tiny inline notice rendered above the Researched Posts / Prompt Posts tabs
 * when the user's plan can't generate new posts. Surfaces the plan reality
 * before they click "Regenerate" and hit a 402.
 */
function PostsUpgradeNotice({ reason, onUpgrade, testId }) {
  return (
    <Card
      className="border-primary/25 bg-gradient-to-br from-[hsl(var(--peach))]/25 via-card to-card mb-5"
      data-testid={testId}
    >
      <CardContent className="p-4 flex flex-wrap items-start sm:items-center gap-3 justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
            <Lock size={14} />
          </span>
          <div className="min-w-0">
            <Badge
              variant="secondary"
              className="font-mono-edge text-[10px] tracking-[0.16em]"
            >
              EXECUTIVE THOUGHT LEADERSHIP
            </Badge>
            <p className="font-semibold text-sm mt-1">
              Generating new posts is on the Executive Thought Leadership Plan.
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {reason} Upgrade to Executive Thought Leadership for 40 posts/month
              + 12 carousels/month, all written in your voice.
            </p>
          </div>
        </div>
        <Button
          onClick={onUpgrade}
          className="rounded-xl shrink-0"
          data-testid={`${testId}-cta`}
        >
          Upgrade to Executive Thought Leadership <ArrowUpRight size={14} className="ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}
